import { createStyles } from "@mantine/core";

const useStyles = createStyles((theme) => ({
  content: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    overflowX: "auto",
    backgroundColor: "var(--app-bg)",
    minHeight: "100dvh",
    fontFamily: "Inter, sans-serif",
    fontSize: 14,
    lineHeight: 1.5,
  },
  wrap: {
    padding: "26px 28px 40px",
  },

  // ---- create / report / settings row ----
  pageActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 18,
    flexWrap: "wrap",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: "12px",
    boxShadow: "0 1px 2px rgba(16, 24, 40, .05)",
    padding: "10px 12px",
  },
  spacer: {
    flex: 1,
  },
  btnPrimary: {
    backgroundColor: "var(--app-primary) !important",
    border: "1px solid var(--app-primary)",
    color: "var(--app-on-primary) !important",
    fontSize: 13,
    fontWeight: 600,
    padding: "9px 14px",
    borderRadius: "9px",
    "&:hover": {
      backgroundColor: "#F59E2E !important",
    },
  },
  btnGhost: {
    backgroundColor: "var(--app-surface) !important",
    border: "1px solid var(--app-border)",
    color: "var(--app-text) !important",
    fontSize: 13,
    fontWeight: 600,
    padding: "9px 14px",
    borderRadius: "9px",
    "&:hover": {
      backgroundColor: "var(--app-surface-2) !important",
    },
  },

  // ---- KPI cards ----
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 14,
    marginBottom: 18,
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media (max-width: 560px)": {
      gridTemplateColumns: "1fr",
    },
  },
  kpi: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: "12px",
    padding: "15px 16px",
    boxShadow: "0 1px 2px rgba(16, 24, 40, .05)",
  },
  kpiTop: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    marginBottom: 10,
  },
  kpiIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
    flex: "none",
  },
  kpiIconInfo: {
    background: "var(--app-tint-blue)",
    color: "var(--app-ink-blue)",
  },
  kpiIconWarn: {
    background: "var(--app-tint-amber)",
    color: "var(--app-ink-amber)",
  },
  kpiIconDanger: {
    background: "var(--app-tint-red)",
    color: "var(--app-ink-red)",
  },
  kpiLabel: {
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    fontWeight: 500,
  },
  kpiVal: {
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    lineHeight: 1,
    color: "var(--app-text)",
  },
  kpiSub: {
    fontSize: 11.5,
    color: "var(--app-text-faint, #8A93A2)",
    marginTop: 6,
    "& b": {
      fontWeight: 700,
      color: "var(--app-text)",
    },
  },

  // ---- toolbar (status tabs + search + filter) ----
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: "12px",
    boxShadow: "0 1px 2px rgba(16, 24, 40, .05)",
    padding: "10px 12px",
  },
  statusSeg: {
    display: "inline-flex",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: 9,
    padding: 3,
    flexWrap: "wrap",
  },
  statusBtn: {
    border: "none",
    background: "transparent",
    color: "var(--app-text-muted)",
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 600,
    padding: "7px 12px",
    borderRadius: 6,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    '&[data-active="true"]': {
      background: "var(--app-surface)",
      color: "var(--app-text)",
      boxShadow: "0 1px 2px rgba(16, 24, 40, .05)",
    },
  },
  statusCount: {
    fontSize: 11,
    color: "var(--app-text-faint, #8A93A2)",
  },
  searchBox: {
    flex: 1,
    minWidth: 240,
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: "9px",
    padding: "8px 12px",
    color: "var(--app-text-faint, #8A93A2)",
    fontSize: 13,
    "& input": {
      border: "none",
      outline: "none",
      background: "transparent",
      fontFamily: "inherit",
      fontSize: 13,
      color: "var(--app-text)",
      flex: 1,
    },
  },
  fcount: {
    background: "var(--app-primary)",
    color: "var(--app-on-primary)",
    fontSize: 11,
    fontWeight: 800,
    borderRadius: 20,
    padding: "1px 7px",
    marginLeft: 2,
  },

  // ---- table card ----
  tableCard: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: "12px",
    boxShadow: "0 1px 2px rgba(16, 24, 40, .05)",
    overflow: "hidden",
  },
  paginationFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderTop: "1px solid var(--app-border)",
    fontSize: 12.5,
    color: "var(--app-text-muted)",
  },

  // ---- filter / sort popovers ----
  FilterIcon: {
    padding: "5px",
    borderRadius: 3,
    cursor: "pointer",
    color: "var(--app-text-muted)",
  },
  box_header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "13px 15px",
    borderBottom: "1px solid var(--app-border)",
    "& .mantine-Text-root": {
      color: "var(--app-text)",
      fontWeight: 700,
      fontSize: 13.5,
    },
  },
  box_body: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    background: "var(--app-bg)",
    padding: "12px 15px",
  },
  filterFields: {
    padding: "12px",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: "8px",
  },
  btn_container: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 15px",
    borderTop: "1px solid var(--app-border)",
  },
  filterCategoryList: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  filterCategoryItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 15px",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    color: "var(--app-text)",
    borderBottom: "1px solid var(--app-border)",
    "&:hover": {
      backgroundColor: "var(--app-surface-2)",
    },
  },
  activeFilterDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "var(--app-primary)",
  },
  filterCategoryHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "16px 15px",
    borderBottom: "1px solid var(--app-border)",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    color: "var(--app-text)",
  },
  activeFiltersBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 14,
  },
  filterChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 16,
    backgroundColor: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--app-text)",
    "& svg": {
      cursor: "pointer",
      color: "var(--app-text-faint, #8A93A2)",
      "&:hover": {
        color: "var(--app-text)",
      },
    },
  },
  clearAllLink: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },

  // ---- "Create Note" split menu (mockup's .newwrap/.newmenu/.newmenu-item) ----
  newMenuDropdown: {
    width: "300px !important",
    borderRadius: "12px !important",
    padding: "6px !important",
    border: "1px solid var(--app-border) !important",
    boxShadow: "0 12px 32px rgba(16, 24, 40, .16) !important",
  },
  // Plain custom button (not Mantine's Menu.Item) so the DOM/CSS matches the
  // mockup's <button class="newmenu-item"> exactly — Menu.Item's own
  // icon/label wrapper markup fights a literal 1:1 match.
  newMenuItemBtn: {
    display: "flex",
    gap: 11,
    alignItems: "center",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderRadius: 9,
    padding: "10px 11px",
    cursor: "pointer",
    fontFamily: "inherit",
    color: "var(--app-text)",
    "&:hover": {
      backgroundColor: "var(--app-bg)",
    },
  },
  // Mockup uses `.newmenu-item + .newmenu-item{border-top:...}` (adjacent
  // sibling) — applied directly to the second button instead.
  newMenuItemBordered: {
    borderTop: "1px solid var(--app-border)",
    borderRadius: "0 0 9px 9px",
  },
  newMenuIco: {
    flex: "none",
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "#FFF3E0",
    color: "#B25E09",
    display: "grid",
    placeItems: "center",
  },
  newMenuInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  newMenuTitle: {
    fontFamily: "Inter",
    fontSize: 13.5,
    fontWeight: 650,
    color: "var(--app-text)",
  },
}));

export const notePaginationStyles = {
  control: {
    minWidth: 30,
    height: 30,
    border: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface)",
    borderRadius: 7,
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    fontWeight: 400,
    "&[data-active]": {
      backgroundColor: "var(--app-primary-soft, #FFF6E9)",
      borderColor: "var(--app-primary-bd, #FFE2B8)",
      color: "#B4700A",
      fontWeight: 700,
    },
  },
};

export default useStyles;
